import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Clock3, Globe, KeyRound, Loader2, Lock, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import api, { apiError } from "../api";

const SLIDES = [
  "https://images.unsplash.com/photo-1584433144859-1fc3ab64a957?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1633265486064-086b219458ec?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1590065707046-4fde65275b2e?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1614064548237-096f735f344f?crop=entropy&cs=srgb&fm=jpg&q=85",
];

function PanamaClock({ testid = "panama-clock", dark = false, compact = false }) {
  const { t, lang } = useLang();
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(i);
  }, []);
  const fmt = new Intl.DateTimeFormat(lang === "es" ? "es-PA" : "en-US", {
    timeZone: "America/Panama",
    ...(compact
      ? { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }
      : { weekday: "short", year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }),
  });
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap" data-testid={testid}>
      <Clock3 className={`w-3.5 h-3.5 shrink-0 ${dark ? "text-[#E9AB53]" : "text-[#044CBC]"}`} />
      {!compact && <span>{t("auth.panama_time")}:</span>}
      <span className={`font-mono text-xs font-semibold ${dark ? "text-white" : "text-[#16335F]"}`}>{fmt.format(now)}</span>
    </span>
  );
}

function LangButtons({ testid }) {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center gap-1 rounded-full border border-[#E2E8F0] bg-white shadow-sm py-0.5 px-1" data-testid={testid}>
      <Globe className="w-3.5 h-3.5 text-[#64748B] ml-1" />
      {["es", "en"].map((l) => (
        <button
          key={l}
          data-testid={`${testid}-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1 text-[11px] font-bold uppercase rounded-full transition-colors duration-200 ${
            lang === l ? "bg-[#044CBC] text-white shadow-sm" : "text-[#64748B] hover:text-[#16335F]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export default function Login() {
  const { t, lang } = useLang();
  const { login, verifyOtp, register } = useAuth();
  const [otp, setOtp] = useState(null);
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const goAfterLogin = (u) => navigate(u?.role === "superadmin" ? "/plataforma" : u?.role === "provider" ? "/portal" : u?.role === "client" ? ((u?.modules || []).length > 0 ? "/" : "/portal") : "/");
  const submitOtp = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try { goAfterLogin(await verifyOtp(otp.pending_token, code, remember)); } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };
  const resendOtp = async () => {
    setError("");
    try { const { data } = await api.post("/auth/resend-otp", { pending_token: otp.pending_token }); setOtp(data); setCode(""); toast.success(t("auth.otp_resent")); } catch (err) { setError(apiError(err)); }
  };
  const navigate = useNavigate();
  const [mode, setMode] = useState(() => (new URLSearchParams(window.location.search).get("mode") === "register" ? "register" : "login"));
  const [form, setForm] = useState({ email: "", password: "", name: "", tenant_name: "", ruc: "", phone: "", contact_person: "" });
  const [logo, setLogo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [resetToken] = useState(() => new URLSearchParams(window.location.search).get("reset") || "");
  const [newPwd, setNewPwd] = useState({ a: "", b: "" });
  const [resetDone, setResetDone] = useState(false);
  const submitForgot = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email: form.email, lang }); setForgotSent(true); } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };
  const submitReset = async (e) => {
    e.preventDefault();
    setError("");
    if (newPwd.a !== newPwd.b) { setError(t("auth.reset_mismatch")); return; }
    setBusy(true);
    try { await api.post("/auth/reset-password", { token: resetToken, new_password: newPwd.a }); setResetDone(true); } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };
  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    if (!pending) return undefined;
    setCountdown(5);
    const i = setInterval(() => {
      setCountdown((c) => {
        if (c === null) return null;
        if (c <= 1) {
          clearInterval(i);
          setPending(false);
          setMode("login");
          setError("");
          return null;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(i);
  }, [pending]);
  const [slide, setSlide] = useState(0);

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return setLogo(null);
    if (f.size > 1_300_000 || !(f.type.startsWith("image/") || f.type === "application/pdf")) return setError(t("auth.logo_error"));
    const rd = new FileReader();
    rd.onload = () => setLogo(rd.result);
    rd.readAsDataURL(f);
  };

  useEffect(() => {
    const i = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 5000);
    return () => clearInterval(i);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      let u;
      if (mode === "login") {
        u = await login(form.email, form.password);
      } else {
        u = await register({ email: form.email, password: form.password, name: form.name, tenant_name: form.tenant_name, ruc: form.ruc, phone: form.phone, contact_person: form.contact_person, logo });
      }
      if (u?.otp_required) {
        setOtp(u);
        setCode("");
        return;
      }
      if (u?.pending) {
        setPending(true);
        return;
      }
      goAfterLogin(u);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inp = (key, testid, type = "text", Icon = null, extra = {}) => (
    <div className="relative">
      {Icon && <Icon className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />}
      <input
        className={`ec-input !h-12 lg:!h-11 !text-base lg:!text-sm ${Icon ? "!pl-10" : ""}`}
        type={type}
        value={form[key]}
        onChange={set(key)}
        data-testid={testid}
        {...extra}
      />
    </div>
  );

  const formCard = (
    <div className="w-full max-w-md bg-white rounded-2xl border border-[#E2E8F0] shadow-[0_24px_60px_rgba(22,51,95,0.12)] p-5 lg:p-8 flex flex-col min-h-0 lg:max-h-full" data-testid="login-form-wrap">
      {resetToken ? (
        <form onSubmit={submitReset} className="space-y-4 py-2" data-testid="reset-form">
          <div className="text-center space-y-2">
            <div className="w-12 h-12 rounded-full bg-[#EFF4FC] text-[#16335F] flex items-center justify-center mx-auto"><Lock className="w-6 h-6" /></div>
            <h3 className="font-heading font-extrabold text-slate-900">{t("auth.reset_title")}</h3>
          </div>
          {resetDone ? (
            <div className="text-center space-y-3" data-testid="reset-done">
              <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
              <p className="text-sm text-slate-700">{t("auth.reset_done")}</p>
              <button type="button" onClick={() => navigate("/login", { replace: true }) || window.location.assign("/login")} className="w-full h-11 rounded-lg bg-[#044CBC] text-white font-bold" data-testid="reset-go-login">{t("auth.login_btn")}</button>
            </div>
          ) : (
            <>
              <input className="ec-input !h-11" type="password" placeholder={t("auth.reset_new")} value={newPwd.a} onChange={(e) => setNewPwd({ ...newPwd, a: e.target.value })} minLength={8} required autoComplete="new-password" data-testid="reset-password-input" />
              <input className="ec-input !h-11" type="password" placeholder={t("auth.reset_confirm")} value={newPwd.b} onChange={(e) => setNewPwd({ ...newPwd, b: e.target.value })} minLength={8} required autoComplete="new-password" data-testid="reset-confirm-input" />
              {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="reset-error">{error}</div>}
              <button type="submit" disabled={busy} className="w-full h-11 rounded-lg bg-[#044CBC] text-white font-bold hover:bg-[#033A8C] disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="reset-submit-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("auth.reset_btn")}</button>
            </>
          )}
        </form>
      ) : forgot ? (
        <form onSubmit={submitForgot} className="space-y-4 py-2" data-testid="forgot-form">
          <div className="text-center space-y-2">
            <div className="w-12 h-12 rounded-full bg-[#EFF4FC] text-[#16335F] flex items-center justify-center mx-auto"><Mail className="w-6 h-6" /></div>
            <h3 className="font-heading font-extrabold text-slate-900">{t("auth.forgot_title")}</h3>
            <p className="text-sm text-[#64748B]">{t("auth.forgot_help")}</p>
          </div>
          {forgotSent ? (
            <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-3" data-testid="forgot-sent">{t("auth.forgot_sent")}</div>
          ) : (
            <>
              <input className="ec-input !h-11" type="email" value={form.email} onChange={set("email")} placeholder={t("auth.email")} required autoComplete="email" data-testid="forgot-email-input" />
              {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="forgot-error">{error}</div>}
              <button type="submit" disabled={busy} className="w-full h-11 rounded-lg bg-[#044CBC] text-white font-bold hover:bg-[#033A8C] disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="forgot-submit-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("auth.forgot_btn")}</button>
            </>
          )}
          <button type="button" onClick={() => { setForgot(false); setForgotSent(false); setError(""); }} className="w-full text-xs text-[#64748B] hover:underline min-h-[44px]" data-testid="forgot-back-button">{t("auth.otp_back")}</button>
        </form>
      ) : otp ? (
        <form onSubmit={submitOtp} className="space-y-4 py-2" data-testid="otp-form">
          <div className="text-center space-y-2">
            <div className="w-12 h-12 rounded-full bg-[#EFF4FC] text-[#16335F] flex items-center justify-center mx-auto"><KeyRound className="w-6 h-6" /></div>
            <h3 className="font-heading font-extrabold text-slate-900">{t("auth.otp_title")}</h3>
            <p className="text-sm text-[#64748B]">{t("auth.otp_help")} <b data-testid="otp-email-hint">{otp.email_hint}</b></p>
          </div>
          <input className="ec-input !h-14 text-center text-2xl font-mono tracking-[0.5em]" inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} value={code}
                 onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="••••••" autoFocus required data-testid="otp-code-input" />
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="otp-error">{error}</div>}
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer min-h-[44px]" data-testid="otp-remember-label">
            <input type="checkbox" className="accent-[#044CBC] w-4 h-4" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="otp-remember-checkbox" />
            {t("auth.otp_remember")}
          </label>
          <button type="submit" disabled={busy || code.length !== 6} className="w-full h-11 rounded-lg bg-[#044CBC] text-white font-bold hover:bg-[#033A8C] disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="otp-submit-button">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {t("auth.otp_verify")}
          </button>
          <div className="flex items-center justify-between text-xs">
            <button type="button" onClick={resendOtp} className="text-[#044CBC] font-bold hover:underline min-h-[44px]" data-testid="otp-resend-button">{t("auth.otp_resend")}</button>
            <button type="button" onClick={() => { setOtp(null); setCode(""); setError(""); }} className="text-[#64748B] hover:underline min-h-[44px]" data-testid="otp-back-button">{t("auth.otp_back")}</button>
          </div>
          <p className="text-[11px] text-[#94A3B8] text-center">{t("auth.otp_expiry")}</p>
        </form>
      ) : pending ? (
        <div className="text-center py-8 space-y-3" data-testid="register-pending">
          <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
          <h3 className="font-heading font-extrabold text-slate-900">{t("auth.pending_title")}</h3>
          <p className="text-sm text-slate-500">{t("auth.pending_msg")}</p>
          <p className="text-xs font-bold text-[#044CBC]" data-testid="pending-countdown">{t("auth.pending_countdown").replace("{s}", String(countdown ?? 5))}</p>
          <button onClick={() => { setPending(false); setMode("login"); setError(""); }} data-testid="pending-back-button" className="px-5 py-2.5 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#044CBC] transition-colors">
            {t("auth.back_home")}
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 rounded-xl bg-[#F1F5F9] p-1 mb-5 gap-1" data-testid="auth-tabs">
            <button
              data-testid="login-tab"
              onClick={() => { setMode("login"); setError(""); }}
              className={`w-full min-h-[44px] px-2 text-sm font-bold rounded-lg transition-all duration-200 leading-tight ${mode === "login" ? "bg-[#16335F] text-white shadow" : "text-[#64748B] hover:text-[#16335F] border border-transparent"}`}
            >
              {t("auth.login_title")}
            </button>
            <button
              data-testid="register-tab"
              onClick={() => { setMode("register"); setError(""); }}
              className={`w-full min-h-[44px] px-2 text-sm font-bold rounded-lg transition-all duration-200 leading-tight ${mode === "register" ? "bg-[#16335F] text-white shadow" : "text-[#64748B] hover:text-[#16335F] border border-transparent"}`}
            >
              {t("auth.register_title")}
            </button>
          </div>
          <form onSubmit={submit} className="space-y-3.5 lg:overflow-y-auto min-h-0 pr-0.5">
            {mode === "register" && (
              <>
                <div>
                  <label className="ec-label">{t("auth.company")}</label>
                  {inp("tenant_name", "register-company-input", "text", null, { required: true })}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="ec-label">{t("auth.ruc")}</label>
                    {inp("ruc", "register-ruc-input")}
                  </div>
                  <div>
                    <label className="ec-label">{t("auth.phone")}</label>
                    {inp("phone", "register-phone-input")}
                  </div>
                </div>
                <div>
                  <label className="ec-label">{t("auth.contact_person")}</label>
                  {inp("contact_person", "register-contact-input")}
                </div>
                <div>
                  <label className="ec-label">{t("auth.logo")}</label>
                  <div className="flex items-center gap-3">
                    <input className="ec-input" type="file" accept="image/*,application/pdf" onChange={onLogo} data-testid="register-logo-input" />
                    {logo && (logo.startsWith("data:application/pdf") ? (
                      <span className="w-10 h-10 rounded-lg border border-[#E2E8F0] bg-white p-0.5 flex items-center justify-center text-[9px] font-bold text-[#044CBC]" data-testid="register-logo-preview">PDF</span>
                    ) : (
                      <img src={logo} alt="logo" className="w-10 h-10 rounded-lg object-contain border border-[#E2E8F0] bg-white p-0.5" data-testid="register-logo-preview" />
                    ))}
                  </div>
                  <p className="text-[10px] text-[#94A3B8] mt-1">{t("auth.logo_hint")}</p>
                </div>
                <div>
                  <label className="ec-label">{t("auth.name")}</label>
                  {inp("name", "register-name-input", "text", null, { required: true })}
                </div>
              </>
            )}
            <div>
              <label className="ec-label">{t("auth.email")}</label>
              {inp("email", mode === "login" ? "login-email-input" : "register-email-input", "email", Mail, { required: true })}
            </div>
            <div>
              <label className="ec-label">{t("auth.password")}</label>
              {inp("password", mode === "login" ? "login-password-input" : "register-password-input", "password", Lock, { required: true, minLength: 8 })}
            </div>
            {error && (
              <div className="text-sm text-[#991B1B] bg-[#FEF2F2] border border-[#FCA5A5] rounded-lg px-3 py-2" data-testid="auth-error-alert">{error}</div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid={mode === "login" ? "login-submit-button" : "register-submit-button"}
              className="w-full h-12 lg:h-11 rounded-xl bg-[#044CBC] text-white font-bold text-base lg:text-sm hover:bg-[#033A8C] active:scale-[0.98] transition-all shadow-md shadow-[#044CBC]/25 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? t("auth.login_btn") : t("auth.register_btn")}
            </button>
            {mode === "login" && (
              <button type="button" onClick={() => { setForgot(true); setError(""); }} className="w-full text-sm text-[#044CBC] font-semibold hover:underline min-h-[44px]" data-testid="forgot-link">{t("auth.forgot_link")}</button>
            )}
          </form>
        </>
      )}
    </div>
  );

  return (
    <div className="min-h-[100dvh] lg:h-screen flex flex-col lg:flex-row bg-[#F8FAFC] overflow-x-hidden lg:overflow-hidden" data-testid="login-page">
      {/* Panel de marca — desktop */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden flex-col justify-between p-10 lg:p-14 bg-gradient-to-br from-[#F1F5F9] via-[#F8FAFC] to-[#EFF6FF] border-r border-slate-200">
        <div className="absolute inset-0 opacity-60" style={{ backgroundImage: "linear-gradient(#E2E8F0 1px, transparent 1px), linear-gradient(90deg, #E2E8F0 1px, transparent 1px)", backgroundSize: "44px 44px" }} />
        {SLIDES.map((src, i) => (
          <img
            key={src}
            src={src}
            alt=""
            data-testid={`login-slide-${i}`}
            className={`absolute inset-0 w-full h-full object-cover mix-blend-multiply transition-opacity duration-1000 ${i === slide ? "opacity-[0.14]" : "opacity-0"}`}
          />
        ))}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#044CBC]/10 blur-3xl" />
        <div className="absolute bottom-10 -left-20 w-72 h-72 rounded-full bg-[#E9AB53]/10 blur-3xl" />

        <div className="relative z-10 flex items-center gap-3 animate-[fadeIn_0.6s_ease-out]">
          <img src="/logo.png" alt="KYCFederal" className="h-16 w-auto object-contain" data-testid="brand-logo" />
          <div className="ml-2 flex items-center gap-3 pl-4 border-l border-[#CBD5E1]" data-testid="ssnf-brand-lockup">
            <span className="font-heading text-2xl font-extrabold tracking-tight text-[#033A8C]">SSNF</span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] leading-snug text-[#64748B] max-w-[190px]">
              {t("brand.ssnf")}
            </span>
          </div>
        </div>

        <div className="relative z-10 my-8">
          <div className="w-14 h-1 rounded-full bg-[#E9AB53] mb-6" />
          <h1 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight text-[#16335F] leading-tight animate-[fadeIn_0.8s_ease-out]">
            {t("auth.subtitle")}
          </h1>
          <ul className="mt-7 space-y-2.5">
            {[t("auth.b1"), t("auth.b2"), t("auth.b3")].map((b, i) => (
              <li
                key={b}
                className="flex items-center gap-3 bg-white/85 backdrop-blur rounded-xl border border-[#E2E8F0] shadow-sm px-4 py-2.5 text-sm font-medium text-[#475569] animate-[fadeIn_0.6s_ease-out]"
                style={{ animationDelay: `${0.2 + i * 0.15}s`, animationFillMode: "backwards" }}
              >
                <CheckCircle2 className="w-5 h-5 text-[#044CBC] shrink-0" /> {b}
              </li>
            ))}
          </ul>
          <div className="mt-7 animate-[fadeIn_0.6s_ease-out]" style={{ animationDelay: "0.7s", animationFillMode: "backwards" }}>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2.5">{t("auth.compliance")}</div>
            <div className="flex flex-wrap gap-2" data-testid="compliance-badges">
              {["Resolución S-037-2026 SSNF", "Ley 23 de 2015", "Ley 124 de 2020", "Decreto Ejecutivo 35 de 2022"].map((c) => (
                <span key={c} className="text-[11px] font-semibold px-3 py-1 rounded-full border border-[#E9AB53]/50 bg-[#FFF7EC] text-[#16335F] tracking-wide">{c}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="relative z-10 border-t border-[#E2E8F0] pt-4" data-testid="login-footer">
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4">
            <div className="space-y-2 text-[11px] text-[#64748B]">
              <PanamaClock />
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-semibold tracking-wide text-[#475569]">OFAC · CSNU · GAFI · FBI · INTERPOL · PEP · BANCO MUNDIAL · OSINT · NAMUS 2.0 · ICMP · CYBER CISA CENTRAL · NTAS</span>
                <span className="text-[#CBD5E1]">|</span>
                <span>© <span className="font-semibold text-[#16335F]" data-testid="app-version">{t("auth.version")}</span></span>
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <a href="mailto:cumplimiento@kycfederal.com" data-testid="login-contact-email" className="inline-flex items-center gap-1.5 hover:text-[#033A8C] transition-colors">
                  <Mail className="w-3.5 h-3.5 text-[#044CBC] shrink-0" /> cumplimiento@kycfederal.com
                </a>
                <span className="inline-flex items-center gap-1.5" data-testid="login-contact-address">
                  <MapPin className="w-3.5 h-3.5 text-[#044CBC] shrink-0" /> Headquarters (HQ) Office, PH Atrium Tower, Obarrio, Ciudad de Panamá, Oficina 29. Piso 26.
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <img src="/wca-assoc.png" alt="World Compliance Association - Organización Asociada" className="h-12 w-auto object-contain" data-testid="wca-logo" />
              <img src="/iso27001-transparent.png" alt="ISO 27001 Bureau Veritas Certification" className="h-12 w-auto object-contain" data-testid="iso-logo" />
              <img src="/latca-transparent.png" alt="LATCA — Latin American Compliance Association" className="h-11 w-auto object-contain" data-testid="latca-logo" />
              <img src="/asocupa-transparent.png" alt="ASOCUPA" className="h-8 w-auto object-contain" data-testid="asocupa-logo" />
            </div>
          </div>
        </div>
      </div>

      {/* Header móvil: fila 1 logo + idioma; fila 2 SSNF + reloj */}
      <div className="lg:hidden px-4 pt-[max(12px,env(safe-area-inset-top))] pb-3 space-y-3 bg-white border-b border-[#E2E8F0]" data-testid="mobile-brand-header">
        <div className="flex items-center justify-between gap-3">
          <img src="/logo.png" alt="KYCFederal" className="h-9 w-auto max-w-[55%] object-contain" />
          <LangButtons testid="language-switcher-mobile" />
        </div>
        <div className="space-y-2" data-testid="mobile-brand-sub">
          <div className="flex items-center gap-2.5" data-testid="ssnf-brand-lockup-mobile">
            <span className="font-heading text-base font-extrabold tracking-tight text-[#033A8C] shrink-0">SSNF</span>
            <span className="text-[9px] font-semibold uppercase tracking-[0.12em] leading-tight text-[#64748B] break-words">{t("brand.ssnf")}</span>
          </div>
          <span className="inline-flex text-[10px] text-[#64748B] bg-[#F8FAFC] border border-[#E2E8F0] rounded-full px-2.5 py-1">
            <PanamaClock testid="panama-clock-mobile" compact />
          </span>
        </div>
      </div>

      {/* Panel de formulario */}
      <div className="flex-1 min-h-0 lg:w-[45%] flex flex-col items-center justify-start lg:justify-center px-4 pb-4 pt-4 lg:pt-12 lg:p-12 bg-white lg:bg-white">
        <div className="hidden lg:flex w-full max-w-md justify-end mb-4">
          <LangButtons testid="language-switcher" />
        </div>
        {formCard}
      </div>

      {/* Features + normativas — móvil, apiladas */}
      <div className="lg:hidden px-4 pt-4 pb-3 space-y-2 bg-[#F8FAFC] border-t border-[#E2E8F0]" data-testid="mobile-features">
        {[t("auth.b1"), t("auth.b2"), t("auth.b3")].map((b) => (
          <div key={b} className="flex items-start gap-2.5 text-sm font-medium text-[#475569] bg-white border border-[#E2E8F0] rounded-xl px-3 py-2.5">
            <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#044CBC] shrink-0" /> <span className="min-w-0 break-words">{b}</span>
          </div>
        ))}
      </div>
      <div className="lg:hidden px-4 pb-4 bg-[#F8FAFC]" data-testid="compliance-badges-mobile">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">{t("auth.compliance")}</div>
        <div className="flex flex-col gap-1.5">
          {["Resolución S-037-2026 SSNF", "Ley 23 de 2015", "Ley 124 de 2020", "Decreto Ejecutivo 35 de 2022"].map((c) => (
            <span key={c} className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-[#E9AB53]/50 bg-[#FFF7EC] text-[#16335F] tracking-wide text-center">{c}</span>
          ))}
        </div>
      </div>

      {/* Footer móvil */}
      <div className="lg:hidden px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-3 bg-[#F8FAFC] border-t border-[#E2E8F0] space-y-3" data-testid="mobile-footer">
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <img src="/wca-assoc.png" alt="WCA" className="h-7 w-auto object-contain" data-testid="wca-logo-mobile" />
          <img src="/iso27001-transparent.png" alt="ISO 27001" className="h-7 w-auto object-contain" data-testid="iso-logo-mobile" />
          <img src="/latca-transparent.png" alt="LATCA" className="h-7 w-auto object-contain" data-testid="latca-logo-mobile" />
          <img src="/asocupa-transparent.png" alt="ASOCUPA" className="h-5 w-auto object-contain" data-testid="asocupa-logo-mobile" />
        </div>
        <div className="flex items-center justify-center gap-2 flex-wrap text-[11px] text-[#64748B]">
          <span className="px-2.5 py-0.5 rounded-full border border-[#E9AB53]/50 bg-[#FFF7EC] text-[#16335F] font-semibold">SSNF Panamá</span>
          <span>© <span className="font-semibold text-[#16335F]" data-testid="app-version-mobile">{t("auth.version")}</span></span>
        </div>
        <a href="mailto:cumplimiento@kycfederal.com" className="flex items-center justify-center gap-1.5 text-[11px] text-[#64748B]" data-testid="login-contact-email-mobile">
          <Mail className="w-3.5 h-3.5 text-[#044CBC]" /> cumplimiento@kycfederal.com
        </a>
      </div>
    </div>
  );
}
